// Tables -> Excel / CSV, one set of rules for the chat export (api/_export.js, "put it in an Excel sheet") and the Export
// buttons of the list pages (DataTable, Documents, Crew Certificates). Plain JS + jszip (src/lib/xlsx.js): runs in the
// browser and on the server. A table = [[header...], [values...]...]; a section = { name, table }.
// Several sections (e.g. "certificates expiring in 90 days" = ship documents + crew certificates): one SHEET per section in
// Excel - the two kinds have different columns, and a sheet each keeps every column meaningful; CSV has no sheets, so it
// gets a first "Section" column with the union of the columns.
import { buildXlsx } from './xlsx.js'

export const EXPORT_MAX_ROWS = 5000
const SKIP = new Set(['id', 'client_id', 'user_id', 'created_by', 'baserow_row_id', 'sort_order', 'list_id', 'deck_id', 'zone_id', 'pin_x', 'pin_y'])
export const label = k => { const s = String(k).replace(/_/g, ' ').trim(); return s.charAt(0).toUpperCase() + s.slice(1) }
// a cell: numbers stay numbers; { value } (a select / link field of the pages) -> its value; a list of plain values -> "a, b"
export function cell(v) {
  if (v === null || v === undefined) return ''
  if (typeof v === 'number') return v
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (Array.isArray(v)) { const u = v.map(x => (x && typeof x === 'object' && 'value' in x ? x.value : x)); return u.every(x => x == null || typeof x !== 'object') ? u.filter(x => x != null && x !== '').join(', ') : JSON.stringify(v) }
  if (typeof v === 'object') return 'value' in v && Object.keys(v).length <= 3 ? cell(v.value) : JSON.stringify(v)
  return String(v)
}
const LIST_KEYS = ['rows', 'items', 'results', 'tasks', 'entries', 'list']
const isRowList = v => Array.isArray(v) && v.some(r => r && typeof r === 'object' && !Array.isArray(r))

// the rows of a read tool's answer (a list, or { rows | items | ... : [...] })
export function rowsOfResult(out) {
  if (Array.isArray(out)) return out
  if (out && typeof out === 'object') for (const k of LIST_KEYS) if (Array.isArray(out[k])) return out[k]
  return []
}
// a read tool's answer as sections: one list -> one section; { vessel: [...], crew: [...] } -> a section per list
// names: { key: 'Section name' } (e.g. { vessel: 'Ship documents', crew: 'Crew certificates' }), name = the one-list name
export function sectionsOfResult(out, name, names = {}) {
  const one = rowsOfResult(out)
  if (one.length || Array.isArray(out) || (out && LIST_KEYS.some(k => Array.isArray(out[k])))) return [{ name, rows: one }]
  if (!out || typeof out !== 'object') return []
  return Object.entries(out).filter(([k, v]) => isRowList(v) || (Array.isArray(v) && k in names)).map(([k, v]) => ({ key: k, name: names[k] || label(k), rows: v }))
}
// [[header...], [values...]...]: the columns asked for (in that order) or every column the rows have, ids left out
// columns: ['key', ...] or [{ key, label }, ...]
export function tableFromRows(rows, columns) {
  const objs = (rows || []).filter(r => r && typeof r === 'object').slice(0, EXPORT_MAX_ROWS)
  let cols = Array.isArray(columns) && columns.length ? columns.map(c => (c && typeof c === 'object' ? { key: String(c.key), label: String(c.label || label(c.key)) } : { key: String(c), label: label(c) })) : []
  if (!cols.length) { const seen = new Set(); for (const r of objs.slice(0, 200)) for (const k of Object.keys(r)) if (!SKIP.has(k) && !k.startsWith('_') && !seen.has(k)) { seen.add(k); cols.push({ key: k, label: label(k) }) } }
  if (!cols.length) return null
  return [cols.map(c => c.label), ...objs.map(r => cols.map(c => cell(r[c.key])))]
}
export function csvText(table) {
  const q = v => { const s = v == null ? '' : String(v); return /[",\n;]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s }
  return '﻿' + table.map(r => r.map(q).join(',')).join('\r\n')
}
// several sections -> one table with a first "Section" column (the union of the columns, in order of first use)
export function mergeSections(sections) {
  if (sections.length === 1) return sections[0].table
  const heads = []
  for (const s of sections) for (const h of s.table[0]) if (!heads.includes(h)) heads.push(h)
  const out = [['Section', ...heads]]
  for (const s of sections) for (const r of s.table.slice(1)) out.push([s.name, ...heads.map(h => { const i = s.table[0].indexOf(h); return i < 0 ? '' : r[i] })])
  return out
}
// sheet names: max 31 characters, no \ / ? * [ ] :, unique
export function sheetNames(sections) {
  const used = new Set()
  return sections.map((s, i) => {
    const base = String(s.name || 'Sheet ' + (i + 1)).replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 28) || 'Sheet ' + (i + 1)
    let n = base, k = 2
    while (used.has(n.toLowerCase())) n = base.slice(0, 26) + ' ' + k++
    used.add(n.toLowerCase())
    return n
  })
}
export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
// sections -> { bytes, mime, ext } for xlsx / csv
export async function sectionsFile(sections, format) {
  if (format === 'csv') return { bytes: new TextEncoder().encode(csvText(mergeSections(sections))), mime: 'text/csv; charset=utf-8', ext: 'csv' }
  const names = sheetNames(sections)
  return { bytes: await buildXlsx(sections.map((s, i) => ({ name: names[i], rows: s.table }))), mime: XLSX_MIME, ext: 'xlsx' }
}
export const exportName = (title, ext, now = new Date()) => (String(title || 'export').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50) || 'export') + '-' + now.toISOString().slice(0, 10) + '.' + ext

// the list pages' Export button: the rows shown (filters applied) with the page's own columns -> a download in the browser
// rows: the display rows ({ 'Column label': value | { value } }); columns: [{ key, label }] (or plain labels)
export async function downloadRows({ title, rows, columns, format = 'xlsx', doc = typeof document !== 'undefined' ? document : null }) {
  const table = tableFromRows(rows, columns)
  if (!table || table.length < 2) throw new Error('Nothing to export - the list is empty.')
  const file = await sectionsFile([{ name: title, table }], format)
  const name = exportName(title, file.ext)
  if (doc) {
    const url = URL.createObjectURL(new Blob([file.bytes], { type: file.mime }))
    const a = doc.createElement('a'); a.href = url; a.download = name; doc.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 4000)
  }
  return { name, rows: table.length - 1, file }
}
